import { useEffect, useState, useCallback } from 'react'

export interface ToastAction {
  label: string
  onClick: () => void
}

export interface ToastData {
  id: string
  message: string
  type: 'success' | 'error' | 'info'
  action?: ToastAction
  duration?: number
}

interface ToastProps {
  toast: ToastData
  onDismiss: (id: string) => void
}

function Toast({ toast, onDismiss }: ToastProps) {
  useEffect(() => {
    const timer = setTimeout(() => {
      onDismiss(toast.id)
    }, toast.duration ?? 5000)

    return () => clearTimeout(timer)
  }, [toast.id, toast.duration, onDismiss])

  const bgColor = {
    success: 'bg-primary-text text-primary-bg',
    error: 'bg-error text-primary-bg',
    info: 'bg-primary-text text-primary-bg',
  }

  return (
    <div
      className={`${bgColor[toast.type]} px-4 py-3 flex items-center justify-between gap-4 min-w-[300px]`}
      role="alert"
    >
      <p className="text-sm">{toast.message}</p>
      {toast.action && (
        <button
          onClick={() => {
            toast.action!.onClick()
            onDismiss(toast.id)
          }}
          className="text-sm underline hover:opacity-80 whitespace-nowrap"
        >
          {toast.action.label}
        </button>
      )}
      <button
        onClick={() => onDismiss(toast.id)}
        className="text-primary-bg hover:opacity-80"
        aria-label="Dismiss"
      >
        &times;
      </button>
    </div>
  )
}

interface ToastContainerProps {
  toasts: ToastData[]
  onDismiss: (id: string) => void
}

export function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  )
}

export function useToast() {
  const [toasts, setToasts] = useState<ToastData[]>([])

  const addToast = useCallback((message: string, type: ToastData['type'] = 'info', options?: { action?: ToastAction; duration?: number }) => {
    const id = Date.now().toString()
    setToasts((prev) => [...prev, { id, message, type, ...options }])
  }, [])

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }, [])

  return { toasts, addToast, dismissToast }
}
